import type { LucideIcon } from 'lucide-react'
import { ArrowLeft, LogOut, Menu, X } from 'lucide-react'
import { type ReactNode, useState } from 'react'
import { Link, NavLink, useNavigate } from 'react-router-dom'
import { useAuth } from '@/auth/auth-context'
import { Logo } from '@/components/logo'
import { cn } from '@/lib/cn'

export interface DashboardNavItem {
  label: string
  to: string
  icon: LucideIcon
  end?: boolean
}

function initialsFor(email: string | undefined) {
  return (email?.[0] ?? '?').toUpperCase()
}

function NavList({
  navItems,
  onNavigate,
}: {
  navItems: DashboardNavItem[]
  onNavigate?: () => void
}) {
  return (
    <nav className="flex flex-1 flex-col gap-1">
      {navItems.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          onClick={onNavigate}
          className={({ isActive }) =>
            cn(
              'group relative flex items-center gap-3 rounded-md py-2.5 pl-4 pr-3 text-sm font-medium transition-colors',
              isActive
                ? 'bg-primary-soft text-primary-hover'
                : 'text-ink-muted hover:bg-paper hover:text-ink',
            )
          }
        >
          {({ isActive }) => (
            <>
              <span
                className={cn(
                  'absolute inset-y-1.5 left-0 w-1 rounded-full bg-accent transition-opacity',
                  isActive ? 'opacity-100' : 'opacity-0',
                )}
                aria-hidden
              />
              <item.icon className="size-4 shrink-0" aria-hidden />
              {item.label}
            </>
          )}
        </NavLink>
      ))}
    </nav>
  )
}

export function DashboardShell({
  navItems,
  children,
}: {
  navItems: DashboardNavItem[]
  children: ReactNode
}) {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [mobileOpen, setMobileOpen] = useState(false)

  async function handleLogout() {
    await logout()
    navigate('/')
  }

  return (
    <div className="flex min-h-screen bg-paper">
      {/* Desktop sidebar */}
      <aside className="hidden w-64 shrink-0 flex-col border-r border-line bg-paper-raised px-4 py-6 md:flex">
        <Link to="/" className="px-2">
          <Logo />
        </Link>

        <div className="mt-6 flex items-center gap-3 rounded-lg bg-paper px-3 py-3">
          <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-accent text-sm font-semibold text-ink">
            {initialsFor(user?.email)}
          </span>
          <p className="min-w-0 truncate text-sm font-medium text-ink">
            {user?.email}
          </p>
        </div>

        <div className="mt-6 flex-1">
          <NavList navItems={navItems} />
        </div>

        <div className="flex flex-col gap-1 border-t border-line pt-4">
          <Link
            to="/"
            className="flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-ink-muted hover:bg-paper hover:text-ink"
          >
            <ArrowLeft className="size-4" aria-hidden />
            Back to site
          </Link>
          <button
            type="button"
            onClick={handleLogout}
            className="flex items-center gap-3 rounded-md px-3 py-2 text-left text-sm font-medium text-danger hover:bg-paper"
          >
            <LogOut className="size-4" aria-hidden />
            Log out
          </button>
        </div>
      </aside>

      {/* Mobile top bar + slide-over nav — the content itself mounts once,
          below, shared with desktop via responsive padding only. Mounting
          `children` a second time here would double-fire every hook inside
          the page (queries, mutations, local state). */}
      <div className="flex flex-1 flex-col">
        <div className="flex shrink-0 items-center justify-between border-b border-line bg-paper-raised px-4 py-3 md:hidden">
          <Link to="/">
            <Logo />
          </Link>
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            className="flex size-9 items-center justify-center rounded-md text-ink hover:bg-paper"
            aria-label="Open menu"
          >
            <Menu className="size-5" aria-hidden />
          </button>
        </div>

        {mobileOpen && (
          <div className="fixed inset-0 z-50 md:hidden">
            <button
              type="button"
              aria-label="Close menu"
              className="absolute inset-0 bg-ink/40"
              onClick={() => setMobileOpen(false)}
            />
            <div className="absolute inset-y-0 right-0 flex w-72 max-w-[85vw] flex-col bg-paper-raised px-4 py-6 shadow-raised">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-accent text-sm font-semibold text-ink">
                    {initialsFor(user?.email)}
                  </span>
                  <p className="max-w-36 truncate text-sm font-medium text-ink">
                    {user?.email}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setMobileOpen(false)}
                  className="flex size-9 items-center justify-center rounded-md text-ink-muted hover:bg-paper"
                  aria-label="Close menu"
                >
                  <X className="size-5" aria-hidden />
                </button>
              </div>

              <div className="mt-6 flex-1">
                <NavList
                  navItems={navItems}
                  onNavigate={() => setMobileOpen(false)}
                />
              </div>

              <div className="flex flex-col gap-1 border-t border-line pt-4">
                <Link
                  to="/"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-ink-muted hover:bg-paper hover:text-ink"
                >
                  <ArrowLeft className="size-4" aria-hidden />
                  Back to site
                </Link>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="flex items-center gap-3 rounded-md px-3 py-2 text-left text-sm font-medium text-danger hover:bg-paper"
                >
                  <LogOut className="size-4" aria-hidden />
                  Log out
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="flex-1 overflow-x-hidden">
          <div className="mx-auto max-w-5xl px-4 py-6 sm:px-6 md:py-8 lg:px-10">
            {children}
          </div>
        </div>
      </div>
    </div>
  )
}
